import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, TextField, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Chip, IconButton, Dialog,
  DialogContent, DialogActions, Grid, InputAdornment,
  CircularProgress, Avatar, Tooltip, Accordion, AccordionSummary,
  AccordionDetails, TablePagination, Button,
} from '@mui/material';
import {
  Search as SearchIcon,
  Visibility as VisibilityIcon,
  ExpandMore as ExpandMoreIcon,
  Close as CloseIcon,
  Gavel as GavelIcon,
  Assignment as AssignmentIcon,
  ArrowBack as ArrowBackIcon,
  People as PeopleIcon,
  PictureAsPdf as PdfIcon,
  Download as DownloadIcon,
  CheckCircle as CheckCircleIcon,
  HourglassEmpty as PendingIcon,
  FolderOpen as FolderIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  Badge as BadgeIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import {
  adminListarServidores,
  adminBuscarDocumentosServidor,
  adminObterPdf,
  getAdminToken,
} from '../../services/adminApi';

// ─── helpers ──────────────────────────────────────────────────────────────────
const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';
const getToken = () => getAdminToken() || '';

const fmt = (d: string) =>
  d ? new Date(d).toLocaleDateString('pt-BR') : '—';

const fmtSize = (b: number) =>
  b ? (b >= 1_048_576 ? `${(b / 1_048_576).toFixed(1)} MB` : `${(b / 1024).toFixed(1)} KB`) : '—';

async function downloadPdfAssinado(
  servidorId: number,
  documentoId: number,
  nomeArquivo: string,
) {
  const res = await fetch(
    `${API_URL}/documentos/download-assinado/${servidorId}/${documentoId}`,
    { headers: { Authorization: `Bearer ${getToken()}` } },
  );
  if (!res.ok) throw new Error('Erro ao baixar PDF');
  const blob = await res.blob();
  const url  = URL.createObjectURL(blob);
  const a    = document.createElement('a');
  a.href     = url;
  a.download = nomeArquivo.replace(/\.pdf$/i, '_assinado.pdf');
  a.click();
  URL.revokeObjectURL(url);
}

async function obterPdfBase64(documentoId: number, servidorId: number): Promise<string> {
  const res = await fetch(`${API_URL}/documentos/${documentoId}/pdf/${servidorId}`, {
    headers: { Authorization: `Bearer ${getToken()}` },
  });
  if (!res.ok) throw new Error('Erro ao obter PDF');
  const data = await res.json();
  return data.pdf_base64;
}

// ─── PDF Viewer ───────────────────────────────────────────────────────────────
const PdfViewerDialog: React.FC<{
  open: boolean; doc: any; servidor: any; onClose: () => void;
}> = ({ open, doc, servidor, onClose }) => {
  const [pdf, setPdf]                 = useState<string | null>(null);
  const [loading, setLoading]         = useState(false);
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    if (open && doc && servidor) loadPdf();
    if (!open) setPdf(null);
  }, [open, doc]);

  const loadPdf = async () => {
    setLoading(true);
    try { setPdf(await obterPdfBase64(doc.id, servidor.id)); }
    catch { toast.error('Erro ao carregar PDF'); }
    finally { setLoading(false); }
  };

  const handleDownload = async () => {
    setDownloading(true);
    try {
      await downloadPdfAssinado(servidor.id, doc.id, doc.nome_arquivo);
      toast.success('Download iniciado!');
    } catch { toast.error('Erro ao baixar PDF'); }
    finally { setDownloading(false); }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth
      PaperProps={{ sx: { borderRadius: 3, overflow: 'hidden' } }}>
      <Box sx={{
        background: 'linear-gradient(135deg, #0D47A1 0%, #1565C0 100%)',
        px: 3, py: 2,
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <PdfIcon sx={{ color: 'rgba(255,255,255,0.85)' }} />
          <Box>
            <Typography variant="subtitle2" fontWeight={700} color="white" sx={{ lineHeight: 1.2 }}>
              {doc?.nome_arquivo}
            </Typography>
            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.65)' }}>
              {servidor?.nome_completo}
            </Typography>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          {doc?.status === 'ASSINADO' && (
            <Button size="small" variant="contained" onClick={handleDownload} disabled={downloading}
              startIcon={downloading ? <CircularProgress size={13} color="inherit" /> : <DownloadIcon />}
              sx={{
                bgcolor: 'rgba(255,255,255,0.15)', border: '1px solid rgba(255,255,255,0.3)',
                color: 'white', fontWeight: 700, borderRadius: 2,
                '&:hover': { bgcolor: 'rgba(255,255,255,0.25)' },
              }}>
              {downloading ? 'Baixando...' : 'Baixar Assinado'}
            </Button>
          )}
          <IconButton onClick={onClose} size="small" sx={{ color: 'white' }}><CloseIcon /></IconButton>
        </Box>
      </Box>

      <DialogContent dividers sx={{ p: 0, bgcolor: '#f0f0f0', minHeight: 560 }}>
        {loading ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: 480, gap: 2 }}>
            <CircularProgress size={40} />
            <Typography variant="body2" color="text.secondary">Carregando documento...</Typography>
          </Box>
        ) : pdf ? (
          <iframe
            src={`data:application/pdf;base64,${pdf}`}
            width="100%" height="560"
            style={{ border: 'none', display: 'block' }}
            title="PDF"
          />
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: 480, gap: 2 }}>
            <PdfIcon sx={{ fontSize: 52, color: 'text.disabled' }} />
            <Typography color="text.secondary">PDF não disponível</Typography>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 1.5, bgcolor: 'grey.50', borderTop: '1px solid', borderColor: 'divider' }}>
        <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
          <Chip label={doc?.status} size="small"
            color={doc?.status === 'ASSINADO' ? 'success' : 'warning'}
            icon={doc?.status === 'ASSINADO' ? <CheckCircleIcon /> : <PendingIcon />} />
          {doc?.data_assinatura && (
            <Typography variant="caption" color="text.secondary">
              Assinado em {fmt(doc.data_assinatura)}
            </Typography>
          )}
        </Box>
        <Button onClick={onClose} sx={{ borderRadius: 2 }}>Fechar</Button>
      </DialogActions>
    </Dialog>
  );
};

// ─── Documentos do servidor ───────────────────────────────────────────────────
const DocsDialog: React.FC<{
  open: boolean;
  servidor: any;
  tipo: 'CONTRATO' | 'SOLICITACAO' | null;
  onClose: () => void;
}> = ({ open, servidor, tipo, onClose }) => {
  const [docs, setDocs]               = useState<any[]>([]);
  const [loading, setLoading]         = useState(false);
  const [viewerDoc, setViewerDoc]     = useState<any>(null);
  const [viewerOpen, setViewerOpen]   = useState(false);
  const [downloading, setDownloading] = useState<number | null>(null);

  useEffect(() => {
    if (open && servidor) load();
    if (!open) setDocs([]);
  }, [open, servidor]);

  const load = async () => {
    setLoading(true);
    try { setDocs(await adminBuscarDocumentosServidor(servidor.id)); }
    catch { toast.error('Erro ao carregar documentos'); }
    finally { setLoading(false); }
  };

  const filtered = tipo ? docs.filter(d => d.tipo === tipo) : docs;

  const handleDownload = async (doc: any) => {
    setDownloading(doc.id);
    try {
      await downloadPdfAssinado(servidor.id, doc.id, doc.nome_arquivo);
      toast.success('Download iniciado!');
    } catch { toast.error('Erro ao baixar'); }
    finally { setDownloading(null); }
  };

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}>
        <Box sx={{
          px: 3, py: 2,
          borderBottom: '1px solid', borderColor: 'divider',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            {tipo === 'CONTRATO'
              ? <GavelIcon sx={{ color: '#7B1FA2' }} />
              : <AssignmentIcon sx={{ color: '#2E7D32' }} />}
            <Box>
              <Typography variant="h6" fontWeight={700}>
                {tipo === 'CONTRATO' ? 'Contratos' : 'Solicitações'}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {servidor?.nome_completo}
              </Typography>
            </Box>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Chip size="small" color="success" variant="outlined"
              label={`${filtered.filter(d => d.status === 'ASSINADO').length} assinados`} />
            <Chip size="small" color="warning" variant="outlined"
              label={`${filtered.filter(d => d.status !== 'ASSINADO').length} pendentes`} />
            <IconButton onClick={onClose} size="small"><CloseIcon /></IconButton>
          </Box>
        </Box>

        <DialogContent sx={{ p: 0 }}>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
              <CircularProgress />
            </Box>
          ) : filtered.length === 0 ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 8, gap: 2 }}>
              <FolderIcon sx={{ fontSize: 52, color: 'text.disabled' }} />
              <Typography color="text.secondary">Nenhum documento encontrado</Typography>
            </Box>
          ) : (
            <TableContainer>
              <Table>
                <TableHead sx={{ bgcolor: 'grey.50' }}>
                  <TableRow>
                    {['Documento', 'Status', 'Criado em', 'Assinado em', 'Tamanho', 'Ações'].map(h => (
                      <TableCell key={h} align={h === 'Ações' ? 'center' : 'left'}
                        sx={{ fontWeight: 700, fontSize: '0.72rem', color: 'text.secondary',
                          textTransform: 'uppercase', letterSpacing: 0.4, py: 1.5 }}>
                        {h}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filtered.map(doc => (
                    <TableRow key={doc.id} hover sx={{ '&:last-child td': { border: 0 } }}>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <PdfIcon sx={{ color: '#E53935', fontSize: 18 }} />
                          <Typography variant="body2" fontWeight={500} noWrap sx={{ maxWidth: 200 }}>
                            {doc.nome_arquivo}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip size="small" label={doc.status}
                          color={doc.status === 'ASSINADO' ? 'success' : 'warning'}
                          icon={doc.status === 'ASSINADO'
                            ? <CheckCircleIcon sx={{ fontSize: '13px !important' }} />
                            : <PendingIcon sx={{ fontSize: '13px !important' }} />} />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">{fmt(doc.created_at)}</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">{fmt(doc.data_assinatura)}</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">{fmtSize(doc.tamanho)}</Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
                          <Tooltip title="Visualizar">
                            <IconButton size="small" color="primary"
                              onClick={() => { setViewerDoc(doc); setViewerOpen(true); }}>
                              <VisibilityIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          {doc.status === 'ASSINADO' && (
                            <Tooltip title="Baixar PDF assinado">
                              <IconButton size="small" color="success"
                                disabled={downloading === doc.id}
                                onClick={() => handleDownload(doc)}>
                                {downloading === doc.id
                                  ? <CircularProgress size={15} />
                                  : <DownloadIcon fontSize="small" />}
                              </IconButton>
                            </Tooltip>
                          )}
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 1.5, borderTop: '1px solid', borderColor: 'divider' }}>
          <Button onClick={onClose} sx={{ borderRadius: 2 }}>Fechar</Button>
        </DialogActions>
      </Dialog>

      <PdfViewerDialog
        open={viewerOpen} doc={viewerDoc} servidor={servidor}
        onClose={() => setViewerOpen(false)}
      />
    </>
  );
};

// ─── Detail Dialog ────────────────────────────────────────────────────────────
const fmtBRL = (v: number) =>
  v != null ? v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' }) : '—';

const DetailDialog: React.FC<{
  open: boolean;
  servidor: any; // shape: { servidor, matriculas, totais }
  loadingDetail: boolean;
  onClose: () => void;
  onDocumentos: (tipo: 'CONTRATO' | 'SOLICITACAO') => void;
}> = ({ open, servidor: payload, loadingDetail, onClose, onDocumentos }) => {
  if (!payload) return null;

  const srv            = payload.servidor ?? payload;
  const matriculas: any[] = payload.matriculas ?? [];
  const totais         = payload.totais ?? {};

  const row = (label: string, value: any, icon?: React.ReactNode) => (
    <Grid item xs={12} sm={6}>
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
        {icon && <Box sx={{ color: 'text.disabled', mt: 0.2, flexShrink: 0 }}>{icon}</Box>}
        <Box>
          <Typography variant="caption" color="text.disabled"
            sx={{ fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.6, fontSize: '0.62rem' }}>
            {label}
          </Typography>
          <Typography variant="body2" fontWeight={500} sx={{ mt: 0.2 }}>
            {value ?? '—'}
          </Typography>
        </Box>
      </Box>
    </Grid>
  );

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth
      PaperProps={{ sx: { borderRadius: 3, overflow: 'hidden' } }}>

      {/* ── Header ── */}
      <Box sx={{
        background: 'linear-gradient(135deg, #0D47A1 0%, #1976D2 100%)',
        px: 3, py: 3,
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Avatar sx={{
            width: 54, height: 54, fontSize: 22, fontWeight: 800,
            bgcolor: 'rgba(255,255,255,0.18)',
            border: '2px solid rgba(255,255,255,0.35)',
            color: 'white',
          }}>
            {srv.nome_completo?.charAt(0)}
          </Avatar>
          <Box>
            <Typography variant="h6" fontWeight={800} color="white" sx={{ lineHeight: 1.2 }}>
              {srv.nome_completo}
            </Typography>
            <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.68)' }}>
              CPF: {srv.cpf}
            </Typography>
          </Box>
        </Box>
        <IconButton onClick={onClose} sx={{ color: 'white' }} size="small">
          <CloseIcon />
        </IconButton>
      </Box>

      <DialogContent sx={{ p: 0 }}>
        {loadingDetail ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 8, gap: 2 }}>
            <CircularProgress size={36} />
            <Typography variant="body2" color="text.secondary">Carregando dados...</Typography>
          </Box>
        ) : (
          <>
            {/* ── 1. Dados Pessoais ── */}
            <Accordion defaultExpanded disableGutters elevation={0}
              sx={{ '&::before': { display: 'none' }, borderBottom: '1px solid', borderColor: 'divider' }}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ px: 3, minHeight: 52 }}>
                <Typography fontWeight={700} variant="body2">Dados Pessoais</Typography>
              </AccordionSummary>
              <AccordionDetails sx={{ px: 3, pb: 3 }}>
                <Grid container spacing={2.5}>
                  {row('Nome Completo', srv.nome_completo)}
                  {row('CPF', srv.cpf)}
                  {/* Matrículas inline — lista os números */}
                  <Grid item xs={12} sm={6}>
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                      <Box sx={{ color: 'text.disabled', mt: 0.2, flexShrink: 0 }}>
                        <BadgeIcon sx={{ fontSize: 15 }} />
                      </Box>
                      <Box>
                        <Typography variant="caption" color="text.disabled"
                          sx={{ fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.6, fontSize: '0.62rem' }}>
                          Matrículas
                        </Typography>
                        <Box sx={{ mt: 0.5, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                          {matriculas.length > 0
                            ? matriculas.map((m: any) => (
                                <Chip key={m.id} size="small" label={m.matricula}
                                  sx={{ bgcolor: '#E3F2FD', color: '#1565C0', fontWeight: 700, fontSize: '0.72rem' }} />
                              ))
                            : <Typography variant="body2" fontWeight={500}>—</Typography>
                          }
                        </Box>
                      </Box>
                    </Box>
                  </Grid>
                  {row('Telefone', srv.telefone, <PhoneIcon sx={{ fontSize: 15 }} />)}
                  {row('E-mail', srv.email, <EmailIcon sx={{ fontSize: 15 }} />)}
                  {row('Estado Civil', srv.estado_civil)}
                  {row('Identidade', srv.identidade)}
                </Grid>
              </AccordionDetails>
            </Accordion>

            {/* ── 2. Endereço ── */}
            <Accordion disableGutters elevation={0}
              sx={{ '&::before': { display: 'none' }, borderBottom: '1px solid', borderColor: 'divider' }}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ px: 3, minHeight: 52 }}>
                <Typography fontWeight={700} variant="body2">Endereço</Typography>
              </AccordionSummary>
              <AccordionDetails sx={{ px: 3, pb: 3 }}>
                <Grid container spacing={2.5}>
                  {row('Logradouro', srv.endereco)}
                  {row('Número', srv.endereco_numero)}
                  {row('Bairro', srv.bairro)}
                  {row('Cidade', srv.cidade)}
                  {row('Estado', srv.estado)}
                  {row('CEP', srv.cep)}
                </Grid>
              </AccordionDetails>
            </Accordion>

            {/* ── 3. Dados Bancários ── */}
            <Accordion disableGutters elevation={0}
              sx={{ '&::before': { display: 'none' }, borderBottom: '1px solid', borderColor: 'divider' }}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ px: 3, minHeight: 52 }}>
                <Typography fontWeight={700} variant="body2">Dados Bancários</Typography>
              </AccordionSummary>
              <AccordionDetails sx={{ px: 3, pb: 3 }}>
                <Grid container spacing={2.5}>
                  {row('Banco', srv.banco)}
                  {row('Agência', srv.agencia)}
                  {row('Conta', srv.conta)}
                  {row('Tipo de Conta', srv.tipo_conta)}
                  {row('PIX', srv.pix)}
                </Grid>
              </AccordionDetails>
            </Accordion>


            {/* ── 5. Documentos ── */}
            <Box sx={{ px: 3, py: 3, bgcolor: '#FAFAFA' }}>
              <Typography variant="overline" color="text.disabled"
                sx={{ fontWeight: 700, letterSpacing: 1.5, fontSize: '0.65rem', display: 'block', mb: 2 }}>
                Documentos
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Button fullWidth variant="outlined" color="secondary"
                    startIcon={<GavelIcon />} onClick={() => onDocumentos('CONTRATO')}
                    sx={{ py: 1.5, borderRadius: 2.5, fontWeight: 700, textTransform: 'none' }}>
                    Contratos
                  </Button>
                </Grid>
                <Grid item xs={6}>
                  <Button fullWidth variant="outlined" color="success"
                    startIcon={<AssignmentIcon />} onClick={() => onDocumentos('SOLICITACAO')}
                    sx={{ py: 1.5, borderRadius: 2.5, fontWeight: 700, textTransform: 'none' }}>
                    Solicitações
                  </Button>
                </Grid>
              </Grid>
            </Box>
          </>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 1.5, borderTop: '1px solid', borderColor: 'divider' }}>
        <Button onClick={onClose} sx={{ borderRadius: 2 }}>Fechar</Button>
      </DialogActions>
    </Dialog>
  );
};

// ─── Main ─────────────────────────────────────────────────────────────────────
const AdvogadoServidoresPage: React.FC = () => {
  const navigate = useNavigate();

  const [servidores, setServidores]       = useState<any[]>([]);
  const [total, setTotal]                 = useState(0);
  const [loading, setLoading]             = useState(true);
  const [page, setPage]                   = useState(0);
  const [rowsPerPage, setRowsPerPage]     = useState(20);
  const [searchInput, setSearchInput]     = useState('');
  const [search, setSearch]               = useState('');
  const [selected, setSelected]           = useState<any>(null);  // payload { servidor, matriculas, totais }
  const [selectedDoc, setSelectedDoc]     = useState<any>(null);  // servidor simples para DocsDialog
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [detailOpen, setDetailOpen]       = useState(false);
  const [docsOpen, setDocsOpen]           = useState(false);
  const [docsTipo, setDocsTipo]           = useState<'CONTRATO' | 'SOLICITACAO' | null>(null);

  // ── debounce search ──
  useEffect(() => {
    const t = setTimeout(() => { setSearch(searchInput); setPage(0); }, 400);
    return () => clearTimeout(t);
  }, [searchInput]);

  // ── carregar lista ──
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await adminListarServidores({ page: page + 1, limit: rowsPerPage, search });
      if (Array.isArray(res)) { setServidores(res); setTotal(res.length); }
      else { setServidores(res.data || []); setTotal(res.total || 0); }
    } catch { toast.error('Erro ao carregar servidores'); }
    finally { setLoading(false); }
  }, [page, rowsPerPage, search]);

  useEffect(() => { load(); }, [load]);

  // ── abrir detalhe: mostra dialog com dados básicos e busca dados completos ──
  const openDetail = async (s: any) => {
    // Inicia com shape consistente usando dados parciais da listagem
    setSelected({ servidor: s, matriculas: [], totais: {} });
    setSelectedDoc(s);
    setDetailOpen(true);
    setLoadingDetail(true);

    console.log('[openDetail] servidor da listagem:', s);
    console.log('[openDetail] token:', getToken());

    try {
      // Tenta buscar por ID primeiro (rota mais comum)
      let res = await fetch(`${API_URL}/servidores/${s.id}`, {
        headers: { Authorization: `Bearer ${getToken()}` },
      });

      console.log('[openDetail] GET /servidores/:id status:', res.status);

      // Se não funcionar, tenta por CPF
      if (!res.ok && s.cpf) {
        res = await fetch(`${API_URL}/servidores/cpf/${s.cpf}`, {
          headers: { Authorization: `Bearer ${getToken()}` },
        });
        console.log('[openDetail] GET /servidores/cpf/:cpf status:', res.status);
      }

      if (res.ok) {
        const data = await res.json();
        // /servidores/cpf/:cpf retorna { servidor, matriculas, totais, tem_contrato }
        // Salva o payload inteiro para o DetailDialog usar servidor + matriculas + totais
        if (data.servidor) {
          setSelected(data);
          setSelectedDoc(data.servidor); // atualiza também o objeto simples para DocsDialog
        } else {
          setSelected({ servidor: data, matriculas: [], totais: {} });
          setSelectedDoc(data);
        }
      } else {
        // Garante shape consistente mesmo com dados parciais
        setSelected((prev: any) => ({ servidor: prev, matriculas: [], totais: {} }));
      }
    } catch (err) {
      console.error('[openDetail] Erro na requisição:', err);
    } finally {
      setLoadingDetail(false);
    }
  };

  // DocsDialog sempre recebe o objeto servidor simples (com .id)
  const openDocs = (s: any, tipo: 'CONTRATO' | 'SOLICITACAO') => {
    const srv = s?.servidor ?? s;
    setSelectedDoc(srv);
    setDocsTipo(tipo);
    setDocsOpen(true);
  };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#F4F6FB' }}>

      {/* ── Top bar ── */}
      <Box sx={{
        background: 'linear-gradient(135deg, #0D47A1 0%, #1565C0 100%)',
        px: { xs: 2, md: 4 }, py: 2,
        display: 'flex', alignItems: 'center', gap: 1.5,
        boxShadow: '0 3px 16px rgba(13,71,161,0.25)',
      }}>
        <Tooltip title="Voltar ao painel">
          <IconButton onClick={() => navigate('/admin/advogado')} size="small" sx={{ color: 'white' }}>
            <ArrowBackIcon />
          </IconButton>
        </Tooltip>
        <PeopleIcon sx={{ color: 'rgba(255,255,255,0.8)', fontSize: 22 }} />
        <Box sx={{ flex: 1 }}>
          <Typography variant="subtitle1" fontWeight={800} color="white" sx={{ lineHeight: 1.1 }}>
            Servidores
          </Typography>
          <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.6)' }}>
            Consulta de cadastros — somente leitura
          </Typography>
        </Box>
        <Chip
          label={loading && total === 0 ? '...' : `${total} servidores`}
          size="small"
          sx={{
            bgcolor: 'rgba(255,255,255,0.15)', color: 'white',
            border: '1px solid rgba(255,255,255,0.25)', fontWeight: 700,
          }}
        />
      </Box>

      {/* ── Content ── */}
      <Box sx={{ px: { xs: 2, md: 4 }, py: 3, maxWidth: 1400, mx: 'auto' }}>
        <Box sx={{
          bgcolor: 'white', borderRadius: 3,
          border: '1px solid', borderColor: 'divider',
          boxShadow: '0 2px 12px rgba(0,0,0,0.05)',
          overflow: 'hidden',
        }}>
          {/* Search */}
          <Box sx={{ px: 3, py: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
            <TextField
              fullWidth
              placeholder="Pesquisar por nome, CPF ou e-mail..."
              value={searchInput}
              onChange={e => setSearchInput(e.target.value)}
              size="small"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: 'text.secondary', fontSize: 20 }} />
                  </InputAdornment>
                ),
                endAdornment: loading && searchInput
                  ? <InputAdornment position="end"><CircularProgress size={16} /></InputAdornment>
                  : null,
                sx: { borderRadius: 2 },
              }}
            />
          </Box>

          {loading && servidores.length === 0 ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 12 }}>
              <CircularProgress size={40} />
            </Box>
          ) : (
            <>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow sx={{ bgcolor: '#F8F9FC' }}>
                      {['Servidor', 'CPF', 'E-mail', 'Telefone', 'Documentos', 'Detalhes'].map(h => (
                        <TableCell key={h}
                          align={h === 'Documentos' || h === 'Detalhes' ? 'center' : 'left'}
                          sx={{
                            fontWeight: 700, fontSize: '0.72rem', color: 'text.secondary',
                            textTransform: 'uppercase', letterSpacing: 0.5, py: 1.5,
                          }}>
                          {h}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>

                  <TableBody sx={{ opacity: loading ? 0.5 : 1, transition: 'opacity 0.2s' }}>
                    {servidores.map(s => (
                      <TableRow key={s.id} hover
                        sx={{ '&:last-child td': { border: 0 }, cursor: 'pointer' }}>

                        {/* ── Nome — clica para abrir detalhe completo ── */}
                        <TableCell onClick={() => openDetail(s)}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                            <Avatar sx={{
                              width: 36, height: 36, fontSize: 14, fontWeight: 700,
                              bgcolor: '#E3F2FD', color: '#1565C0',
                              border: '1.5px solid #1565C020',
                            }}>
                              {s.nome_completo?.charAt(0)}
                            </Avatar>
                            <Box>
                              <Typography variant="body2" fontWeight={700}>{s.nome_completo}</Typography>
                              {s.matricula && (
                                <Typography variant="caption" color="text.secondary">
                                  Mat. {s.matricula}
                                </Typography>
                              )}
                            </Box>
                          </Box>
                        </TableCell>

                        <TableCell onClick={() => openDetail(s)}>
                          <Typography variant="body2"
                            sx={{ fontFamily: 'monospace', color: 'text.secondary', fontSize: '0.82rem' }}>
                            {s.cpf}
                          </Typography>
                        </TableCell>

                        <TableCell onClick={() => openDetail(s)}>
                          <Typography variant="body2" color="text.secondary" noWrap sx={{ maxWidth: 190 }}>
                            {s.email || '—'}
                          </Typography>
                        </TableCell>

                        <TableCell onClick={() => openDetail(s)}>
                          <Typography variant="body2" color="text.secondary">
                            {s.telefone || '—'}
                          </Typography>
                        </TableCell>

                        <TableCell align="center">
                          <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                            <Chip
                              icon={<GavelIcon sx={{ fontSize: '13px !important' }} />}
                              label="Contratos"
                              size="small" color="secondary" variant="outlined"
                              onClick={() => openDocs(s, 'CONTRATO')}
                              sx={{ cursor: 'pointer', fontWeight: 600, fontSize: '0.72rem' }}
                            />
                            <Chip
                              icon={<AssignmentIcon sx={{ fontSize: '13px !important' }} />}
                              label="Solicitações"
                              size="small" color="success" variant="outlined"
                              onClick={() => openDocs(s, 'SOLICITACAO')}
                              sx={{ cursor: 'pointer', fontWeight: 600, fontSize: '0.72rem' }}
                            />
                          </Box>
                        </TableCell>

                        <TableCell align="center">
                          <Tooltip title="Ver dados completos">
                            <IconButton size="small" color="primary" onClick={() => openDetail(s)}>
                              <VisibilityIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))}

                    {servidores.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={6} align="center" sx={{ py: 10 }}>
                          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1.5 }}>
                            <SearchIcon sx={{ fontSize: 44, color: 'text.disabled' }} />
                            <Typography color="text.secondary">Nenhum servidor encontrado</Typography>
                          </Box>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>

              <TablePagination
                component="div" count={total} page={page}
                onPageChange={(_, p) => setPage(p)}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={e => { setRowsPerPage(parseInt(e.target.value)); setPage(0); }}
                rowsPerPageOptions={[10, 20, 50, 100]}
                labelRowsPerPage="Por página:"
                labelDisplayedRows={({ from, to, count }) => `${from}–${to} de ${count}`}
                sx={{ borderTop: '1px solid', borderColor: 'divider' }}
              />
            </>
          )}
        </Box>
      </Box>

      {/* ── Dialogs ── */}
      <DetailDialog
        open={detailOpen}
        servidor={selected}
        loadingDetail={loadingDetail}
        onClose={() => setDetailOpen(false)}
        onDocumentos={tipo => {
          setDetailOpen(false);
          openDocs(selected, tipo); // openDocs já normaliza internamente
        }}
      />
      <DocsDialog
        open={docsOpen}
        servidor={selectedDoc}
        tipo={docsTipo}
        onClose={() => setDocsOpen(false)}
      />
    </Box>
  );
};

export default AdvogadoServidoresPage;